/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React from 'react';

/**
 * Narrowed resource interface for the CoverageBanner component.
 * Consumers pass only the keys this component needs.
 */
export interface ICoverageBannerResources {
    coverageBannerText: string;
    coverageBannerQualify: string;
    coverageEditButton: string;
    coverageCloseAria: string;
}

/**
 * Props for the CoverageBanner component.
 */
export interface ICoverageBannerProps {
    target: number;
    /** Pass -1 to indicate "loading" — renders an em-dash instead of a count. */
    qualifyingCount: number;
    totalCount: number;
    onEdit: () => void;
    onClose: () => void;
    resources: ICoverageBannerResources;
}

/**
 * Applied coverage banner displayed above the product grid.
 * Shows the active target, qualifying product count, and edit/close actions.
 * @param props - The component props.
 * @returns The coverage banner JSX element.
 */
export const CoverageBanner: React.FC<ICoverageBannerProps> = ({ target, qualifyingCount, totalCount, onEdit, onClose, resources }) => {
    const bannerText = resources.coverageBannerText.replace('{0}', target.toLocaleString());
    const qualifyText =
        qualifyingCount < 0
            ? resources.coverageBannerQualify.replace('{0}', '\u2014').replace('{1}', String(totalCount))
            : resources.coverageBannerQualify.replace('{0}', String(qualifyingCount)).replace('{1}', String(totalCount));

    return (
        <div className='ms-coverage-banner' role='status'>
            <div className='ms-coverage-banner__left'>
                <span className='ms-coverage-banner__icon' aria-hidden='true'>
                    &#10003;
                </span>
                <div className='ms-coverage-banner__text'>
                    <span className='ms-coverage-banner__title'>{bannerText}</span>
                    <span className='ms-coverage-banner__subtitle'>{qualifyText}</span>
                </div>
            </div>
            <div className='ms-coverage-banner__actions'>
                <button type='button' className='ms-coverage-banner__edit' onClick={onEdit}>
                    {resources.coverageEditButton}
                </button>
                <button type='button' className='ms-coverage-banner__close' aria-label={resources.coverageCloseAria} onClick={onClose}>
                    &#10005;
                </button>
            </div>
        </div>
    );
};

export default CoverageBanner;
